import { useEffect, useState } from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Users, Mail, Copy, Trash2, Crown, UserPlus, Shield, FolderKanban, ChevronDown, Bell } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = { owner: "Owner", admin: "Admin", member: "Member", viewer: "Viewer" };
const ROLE_COLOR = { owner: "bg-sunny", admin: "bg-orange text-white", member: "bg-white", viewer: "bg-teal" };
const ASSIGNABLE = ["admin", "member", "viewer"];
const RESTRICTABLE = ["member", "viewer"];

export default function TeamPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = () => apiClient.get("/org").then(({ data }) => setData(data)).catch(() => setData(null));
  useEffect(() => {
    load();
    apiClient.get("/projects").then(({ data }) => setProjects(data)).catch(() => setProjects([]));
  }, []);

  const isManager = data?.my_role === "owner" || data?.my_role === "admin";

  const invite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      await apiClient.post("/org/invite", { email, role });
      toast.success("Invite created — copy the link to share");
      setEmail("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setInviting(false);
    }
  };

  const copyLink = async (token) => {
    const link = `${window.location.origin}/invite/${token}`;
    try { await navigator.clipboard.writeText(link); toast.success("Invite link copied"); }
    catch { toast.error("Couldn't copy — link: " + link); }
  };

  const cancelInvite = async (id) => { await apiClient.delete(`/org/invite/${id}`); load(); };
  const removeMember = async (id) => {
    try { await apiClient.delete(`/org/members/${id}`); toast.success("Teammate removed"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const changeRole = async (member, newRole, allowedProjects) => {
    try {
      await apiClient.put(`/org/members/${member.user_id}/role`, { role: newRole, allowed_projects: allowedProjects ?? member.allowed_projects ?? [] });
      toast.success("Role updated");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggleProject = (member, pid) => {
    const current = member.allowed_projects || [];
    const next = current.includes(pid) ? current.filter((x) => x !== pid) : [...current, pid];
    changeRole(member, member.role, next);
  };

  if (!data) return <div className="flex items-center justify-center h-screen"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>;

  return (
    <div>
      <div className="h-20 flex items-center px-6 sm:px-8">
        <div>
          <h1 className="font-display text-3xl font-700 tracking-tight">Team</h1>
          <p className="text-sm text-ink/60">{data.org?.name}</p>
        </div>
      </div>

      <div className="p-6 lg:p-8 pt-2 max-w-3xl space-y-6">
        {/* My notification preference */}
        <MyNotifications />
        {/* Invite */}
        {isManager && (
          <div className="nb-card p-6">
            <h2 className="font-display font-700 text-xl mb-1 flex items-center gap-2"><UserPlus className="h-5 w-5 text-orange" /> Invite a teammate</h2>
            <p className="text-sm text-ink/60 mb-4 font-600">Admins manage everything; Members triage feedback; Viewers get read-only access.</p>
            <form onSubmit={invite} className="flex flex-col sm:flex-row gap-3" data-testid="invite-form">
              <input data-testid="invite-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="nb-input flex-1" placeholder="teammate@company.com" />
              <select data-testid="invite-role-select" value={role} onChange={(e) => setRole(e.target.value)}
                className="nb-input sm:w-36 font-700">
                <option value="admin">Admin</option>
                <option value="member">Member</option>
                <option value="viewer">Viewer</option>
              </select>
              <button type="submit" disabled={inviting} data-testid="invite-submit"
                className="nb-btn bg-orange text-white px-5 py-2.5 text-sm disabled:opacity-60 shrink-0">
                {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4" /> Send invite</>}
              </button>
            </form>
          </div>
        )}

        {/* Members */}
        <div className="nb-card p-0 overflow-hidden">
          <div className="px-6 py-4 border-b-2 border-ink bg-sand">
            <h2 className="font-display font-700 text-xl flex items-center gap-2"><Users className="h-5 w-5" /> Members ({data.members.length})</h2>
          </div>
          <ul className="divide-y-2 divide-ink/10" data-testid="members-list">
            {data.members.map((m) => {
              const isSelf = m.user_id === user?.user_id;
              const canEditThis = isManager && !isSelf && m.role !== "owner";
              const restricted = RESTRICTABLE.includes(m.role);
              return (
                <li key={m.user_id} className="px-6 py-4" data-testid={`member-row-${m.user_id}`}>
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-teal border-2 border-ink flex items-center justify-center text-sm font-800 uppercase">
                      {(m.name || m.email).charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-700 truncate flex items-center gap-1.5">
                        {m.name || m.email}{isSelf && <span className="text-xs text-ink/40">(you)</span>}
                      </p>
                      <p className="text-xs text-ink/55 truncate font-600">{m.email}</p>
                    </div>

                    {canEditThis ? (
                      <select
                        data-testid={`member-role-select-${m.user_id}`}
                        value={m.role}
                        onChange={(e) => changeRole(m, e.target.value)}
                        className="nb-input py-1.5 px-2 text-xs font-700 w-28"
                      >
                        {ASSIGNABLE.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                      </select>
                    ) : (
                      <span className={`flex items-center gap-1 text-xs font-800 px-2.5 py-1 rounded-full border-2 border-ink ${ROLE_COLOR[m.role] || "bg-white"}`}>
                        {m.role === "owner" && <Crown className="h-3 w-3" />}{ROLE_LABEL[m.role] || m.role}
                      </span>
                    )}

                    {canEditThis && restricted && projects.length > 0 && (
                      <button onClick={() => setExpanded(expanded === m.user_id ? null : m.user_id)} data-testid={`member-access-toggle-${m.user_id}`}
                        className="flex items-center gap-1 text-xs font-700 text-ink/60 hover:text-ink">
                        <FolderKanban className="h-3.5 w-3.5" />
                        {(m.allowed_projects?.length || 0) === 0 ? "All projects" : `${m.allowed_projects.length} project${m.allowed_projects.length === 1 ? "" : "s"}`}
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    )}

                    {canEditThis && (
                      <button onClick={() => removeMember(m.user_id)} data-testid={`remove-member-${m.user_id}`} className="text-ink/40 hover:text-danger"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>

                  {/* Per-project access */}
                  {expanded === m.user_id && restricted && (
                    <div className="mt-3 ml-14 bg-sand border-2 border-ink rounded-2xl p-3" data-testid={`member-access-panel-${m.user_id}`}>
                      <p className="text-[11px] font-800 uppercase tracking-wide text-ink/50 mb-2">Project access (none selected = all)</p>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {projects.map((p) => {
                          const checked = (m.allowed_projects || []).includes(p.id);
                          return (
                            <label key={p.id} className="flex items-center gap-2 text-sm font-700 cursor-pointer">
                              <input type="checkbox" checked={checked} onChange={() => toggleProject(m, p.id)} data-testid={`access-${m.user_id}-${p.id}`} className="h-4 w-4 accent-orange" />
                              <span className="truncate">{p.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Pending invites */}
        {data.pending_invites.length > 0 && (
          <div className="nb-card p-0 overflow-hidden" data-testid="pending-invites">
            <div className="px-6 py-4 border-b-2 border-ink bg-sand">
              <h2 className="font-display font-700 text-xl">Pending invites</h2>
            </div>
            <ul className="divide-y-2 divide-ink/10">
              {data.pending_invites.map((inv) => (
                <li key={inv.id} className="flex items-center gap-3 px-6 py-3.5">
                  <Mail className="h-4 w-4 text-ink/40" />
                  <span className="text-sm font-700 flex-1 truncate">{inv.email}</span>
                  <span className="text-xs font-800 px-2 py-0.5 rounded-full border-2 border-ink bg-white">{ROLE_LABEL[inv.role] || inv.role}</span>
                  <button onClick={() => copyLink(inv.token)} data-testid={`copy-invite-${inv.id}`} className="nb-btn bg-teal px-3 py-1.5 text-xs"><Copy className="h-3 w-3" /> Link</button>
                  {isManager && <button onClick={() => cancelInvite(inv.id)} className="text-ink/40 hover:text-danger"><Trash2 className="h-4 w-4" /></button>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function MyNotifications() {
  const { user, setUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const on = user?.notify_feedback !== false;

  const toggle = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.put("/me/preferences", { notify_feedback: !on });
      setUser(data);
      toast.success(!on ? "You'll get new-feedback emails" : "Feedback emails muted");
    } catch {
      toast.error("Couldn't update your preference");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="nb-card p-6" data-testid="my-notifications-card">
      <h2 className="font-display font-700 text-xl mb-1 flex items-center gap-2"><Bell className="h-5 w-5 text-orange" /> My notifications</h2>
      <div className="flex items-center justify-between gap-4 mt-3">
        <div>
          <p className="font-700 text-sm">Email me when new feedback arrives</p>
          <p className="text-xs text-ink/55 font-600">Applies to every project where you're set as a recipient.</p>
        </div>
        <button type="button" onClick={toggle} disabled={saving} data-testid="my-notify-toggle" aria-pressed={on}
          className={`relative h-7 w-12 rounded-full border-2 border-ink transition-colors shrink-0 disabled:opacity-60 ${on ? "bg-grass" : "bg-white"}`}>
          <span className={`absolute top-[2px] h-5 w-5 rounded-full border-2 border-ink bg-white transition-all ${on ? "left-[22px]" : "left-[2px]"}`} />
        </button>
      </div>
    </div>
  );
}
